import { api } from "../lib";

// ==================== TYPES ====================

export enum DiscussionCategory {
  MALIGNANCY = "malignancy",
  IBD = "ibd",
  DYSPLASIA_BARRETTS = "dysplasia_barretts",
  UNUSUAL_COMPLEX = "unusual_complex",
  DIAGNOSTIC_UNCERTAINTY = "diagnostic_uncertainty",
  EDUCATIONAL = "educational",
  EXTERNAL_OPINION = "external_opinion",
  OTHER = "other",
}

/**
 * Discussion workflow status (per C-813 spec Section 6)
 */
export enum DiscussionStatus {
  PENDING = "pending",
  RESOLVED = "resolved",
  CANCELLED = "cancelled",
}

/**
 * Discussion resolution action (per C-813 spec Section 8.1)
 */
export enum DiscussionAction {
  APPROVED = "approved",
  MODIFIED = "modified",
  ADDITIONAL_STAINS = "additional_stains",
  EXTERNAL_CONSULT = "external_consult",
}

export interface InternalDiscussion {
  id: string;
  caseId: string;
  initiatedById: string;
  assignedToId: string | null;
  category: DiscussionCategory | null;
  reason: string;
  resolution: string | null;
  resolvedById: string | null;
  resolvedAt: string | null;
  isResolved: boolean;
  isUrgent: boolean;
  status: DiscussionStatus;
  // Cancellation fields
  cancelledById: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  // Resolution detail fields (per C-813 spec Section 8.1)
  consensusDiagnosis: string | null;
  recommendations: string | null;
  actionTaken: DiscussionAction | null;
  createdAt: string;
  updatedAt: string;
  case?: {
    id: string;
    caseNumber: string;
    procedureName?: string;
    patient?: {
      firstName: string;
      lastName: string;
      dateOfBirth?: string;
      medicalRecordNumber?: string;
    };
    currentStatus?: {
      id: string;
      name: string;
    };
    procedure?: {
      id: string;
      name: string;
    };
    specimens?: Array<{
      id: string;
      number: string;
      source?: {
        id: string;
        name: string;
      };
    }>;
  };
  initiatedBy?: {
    id: string;
    fullName: string;
  };
  assignedTo?: {
    id: string;
    fullName: string;
  } | null;
  resolvedBy?: {
    id: string;
    fullName: string;
  } | null;
  cancelledBy?: {
    id: string;
    fullName: string;
  } | null;
}

/**
 * Message status for optimistic updates in chat UI
 */
export type MessageStatus = "sending" | "sent" | "error";

export interface CaseComment {
  id: string;
  caseId: string;
  userId: string;
  content: string;
  internalDiscussionId: string | null;
  parentCommentId: string | null;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    fullName: string;
  };
  internalDiscussion?: InternalDiscussion | null;
  parentComment?: CaseComment | null;
  // Optimistic update fields (client-side only)
  optimisticStatus?: MessageStatus;
  optimisticTempId?: string;
}

export interface CreateDiscussionRequest {
  caseId: string;
  category?: DiscussionCategory;
  assignedToId?: string;
  isUrgent?: boolean;
  initialComment?: string;
}

export interface ResolveDiscussionRequest {
  resolution?: string; // Optional per C-807 spec
  consensusDiagnosis?: string;
  recommendations?: string;
  actionTaken?: DiscussionAction;
}

export interface CancelDiscussionRequest {
  reason?: string;
}

export interface AddCommentRequest {
  caseId: string;
  content: string;
  discussionId?: string;
  parentCommentId?: string;
  isInternal?: boolean;
}

export interface UpdateCommentRequest {
  content: string;
}

// ==================== DISCUSSION CATEGORY UTILITIES ====================

export const discussionCategoryLabels: Record<DiscussionCategory, string> = {
  [DiscussionCategory.MALIGNANCY]: "Malignancy",
  [DiscussionCategory.IBD]: "IBD",
  [DiscussionCategory.DYSPLASIA_BARRETTS]: "Dysplasia/Barrett's",
  [DiscussionCategory.UNUSUAL_COMPLEX]: "Unusual/Complex",
  [DiscussionCategory.DIAGNOSTIC_UNCERTAINTY]: "Diagnostic Uncertainty",
  [DiscussionCategory.EDUCATIONAL]: "Educational",
  [DiscussionCategory.EXTERNAL_OPINION]: "External Opinion Discrepancy",
  [DiscussionCategory.OTHER]: "Other",
};

export const discussionCategoryDescriptions: Record<
  DiscussionCategory,
  string
> = {
  [DiscussionCategory.MALIGNANCY]: "Cancer diagnosis",
  [DiscussionCategory.IBD]: "Inflammatory bowel disease",
  [DiscussionCategory.DYSPLASIA_BARRETTS]: "Dysplastic changes",
  [DiscussionCategory.UNUSUAL_COMPLEX]: "Outside normal patterns",
  [DiscussionCategory.DIAGNOSTIC_UNCERTAINTY]: "Need second opinion",
  [DiscussionCategory.EDUCATIONAL]: "Interesting case to share",
  [DiscussionCategory.EXTERNAL_OPINION]: "Discrepancy with external opinion",
  [DiscussionCategory.OTHER]: "Other reason not listed above",
};

export const discussionCategoryColors: Record<DiscussionCategory, string> = {
  [DiscussionCategory.MALIGNANCY]: "red",
  [DiscussionCategory.DYSPLASIA_BARRETTS]: "yellow",
  [DiscussionCategory.IBD]: "blue",
  [DiscussionCategory.UNUSUAL_COMPLEX]: "violet",
  [DiscussionCategory.EDUCATIONAL]: "gray",
  [DiscussionCategory.DIAGNOSTIC_UNCERTAINTY]: "orange",
  [DiscussionCategory.EXTERNAL_OPINION]: "teal",
  [DiscussionCategory.OTHER]: "dark",
};

export const discussionCategoryIcons: Record<DiscussionCategory, string> = {
  [DiscussionCategory.MALIGNANCY]: "🔴",
  [DiscussionCategory.DYSPLASIA_BARRETTS]: "🟡",
  [DiscussionCategory.IBD]: "🔵",
  [DiscussionCategory.UNUSUAL_COMPLEX]: "🟣",
  [DiscussionCategory.EDUCATIONAL]: "⚪",
  [DiscussionCategory.DIAGNOSTIC_UNCERTAINTY]: "🟠",
  [DiscussionCategory.EXTERNAL_OPINION]: "🟦",
  [DiscussionCategory.OTHER]: "⬜",
};

// Abbreviated category labels for queue display (per C-813 spec)
export const discussionCategoryAbbreviations: Record<
  DiscussionCategory,
  string
> = {
  [DiscussionCategory.MALIGNANCY]: "MALIG",
  [DiscussionCategory.DYSPLASIA_BARRETTS]: "DYSPL",
  [DiscussionCategory.IBD]: "IBD",
  [DiscussionCategory.UNUSUAL_COMPLEX]: "UNUSUAL",
  [DiscussionCategory.EDUCATIONAL]: "EDUC",
  [DiscussionCategory.DIAGNOSTIC_UNCERTAINTY]: "UNCERTAIN",
  [DiscussionCategory.EXTERNAL_OPINION]: "EXT OPN",
  [DiscussionCategory.OTHER]: "OTHER",
};

// ==================== SERVICE ====================

export const internalDiscussionsService = {
  /**
   * Create a new internal discussion (flag case for discussion)
   */
  createDiscussion: async (
    data: CreateDiscussionRequest
  ): Promise<InternalDiscussion> => {
    return api.post<InternalDiscussion>(
      "/workflows/internal-discussions",
      data
    );
  },

  /**
   * Get all unresolved discussions (discussion queue)
   */
  getQueue: async (): Promise<InternalDiscussion[]> => {
    return api.get<InternalDiscussion[]>(
      "/workflows/internal-discussions/queue"
    );
  },

  /**
   * Get resolved discussions (for Discussion Queue history)
   */
  getResolvedQueue: async (limit?: number): Promise<InternalDiscussion[]> => {
    const params = limit ? `?limit=${limit}` : "";
    return api.get<InternalDiscussion[]>(
      `/workflows/internal-discussions/resolved${params}`
    );
  },

  /**
   * Get discussions assigned to current user
   */
  getMyAssignments: async (): Promise<InternalDiscussion[]> => {
    return api.get<InternalDiscussion[]>(
      "/workflows/internal-discussions/my-assignments"
    );
  },

  /**
   * Get discussions for a specific case
   */
  getByCaseId: async (caseId: string): Promise<InternalDiscussion[]> => {
    return api.get<InternalDiscussion[]>(
      `/workflows/internal-discussions/case/${caseId}`
    );
  },

  /**
   * Get discussions assigned to a specific user
   */
  getAssignedTo: async (userId: string): Promise<InternalDiscussion[]> => {
    return api.get<InternalDiscussion[]>(
      `/workflows/internal-discussions/assigned/${userId}`
    );
  },

  /**
   * Resolve a discussion
   */
  resolveDiscussion: async (
    discussionId: string,
    data: ResolveDiscussionRequest
  ): Promise<InternalDiscussion> => {
    return api.post<InternalDiscussion>(
      `/workflows/internal-discussions/${discussionId}/resolve`,
      data
    );
  },

  /**
   * Cancel a discussion (per C-813 spec Section 6)
   */
  cancelDiscussion: async (
    discussionId: string,
    data: CancelDiscussionRequest
  ): Promise<InternalDiscussion> => {
    return api.post<InternalDiscussion>(
      `/workflows/internal-discussions/${discussionId}/cancel`,
      data
    );
  },

  /**
   * Get count of pending discussions (for stats)
   */
  getPendingCount: async (): Promise<{ count: number }> => {
    return api.get<{ count: number }>(
      "/workflows/internal-discussions/count/pending"
    );
  },

  /**
   * Get comments for a discussion
   */
  getDiscussionComments: async (
    discussionId: string
  ): Promise<CaseComment[]> => {
    return api.get<CaseComment[]>(
      `/workflows/internal-discussions/${discussionId}/comments`
    );
  },

  /**
   * Add a comment to a case (can be linked to a discussion)
   */
  addComment: async (data: AddCommentRequest): Promise<CaseComment> => {
    return api.post<CaseComment>(
      "/workflows/internal-discussions/comments",
      data
    );
  },

  /**
   * Get comments for a case
   */
  getCaseComments: async (caseId: string): Promise<CaseComment[]> => {
    return api.get<CaseComment[]>(
      `/workflows/internal-discussions/comments/case/${caseId}`
    );
  },

  /**
   * Update a comment
   */
  updateComment: async (
    commentId: string,
    data: UpdateCommentRequest
  ): Promise<CaseComment> => {
    return api.put<CaseComment>(
      `/workflows/internal-discussions/comments/${commentId}`,
      data
    );
  },

  /**
   * Delete a comment
   */
  deleteComment: async (commentId: string): Promise<void> => {
    return api.delete(`/workflows/internal-discussions/comments/${commentId}`);
  },
};
